import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import PaymentTimer from "../components/payment/PaymentTimer";
import { getGame } from "../services/gameService";
import { initiatePayment, pollPaymentStatus } from "../services/paymentService";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguagesContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

.co-page {
  min-height:100vh;
  background:#121212;
  color:#fff;
  font-family:'Inter',sans-serif;
  -webkit-font-smoothing:antialiased;
}

/* subtle grid background matching hero */
.co-bg-grid {
  position:fixed; inset:0; z-index:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
  background-size:48px 48px;
}
.co-bg-glow {
  position:fixed; z-index:0; pointer-events:none;
  top:-10%; right:-10%;
  width:600px; height:500px;
  background:radial-gradient(ellipse, rgba(0,120,242,0.07) 0%, transparent 70%);
}

.co-body {
  position:relative; z-index:1;
  max-width:1100px; margin:0 auto;
  padding:100px 80px 80px;
  display:grid;
  grid-template-columns:1fr 400px;
  gap:32px;
  align-items:start;
}

@media(max-width:900px) {
  .co-body { grid-template-columns:1fr; padding:90px 40px 60px; }
  .co-right { order:-1; }
}
@media(max-width:600px) {
  .co-body { padding:90px 20px 60px; }
}

/* ── BACK LINK ─────────────────────────────── */
.co-back {
  display:inline-flex; align-items:center; gap:6px;
  font-size:13px; font-weight:500; color:#6b6b6b;
  text-decoration:none; margin-bottom:24px;
  transition:color 0.15s;
}
.co-back:hover { color:#0078F2; }

/* ── SECTION LABELS ────────────────────────── */
.co-sect-tag {
  font-size:11px; font-weight:700; letter-spacing:1.5px;
  color:#0078F2; text-transform:uppercase; margin-bottom:16px;
}

/* ── GAME SUMMARY CARD ─────────────────────── */
.co-game-card {
  background:#1a1a1a;
  border:1px solid rgba(255,255,255,0.08);
  border-radius:12px;
  overflow:hidden;
  margin-bottom:16px;
}

.co-cover {
  width:100%; aspect-ratio:16/7;
  object-fit:cover;
  background:linear-gradient(135deg,#1a1a1a,#2a2a2a);
  display:flex; align-items:center; justify-content:center;
  font-size:56px;
}
.co-cover img { width:100%; height:100%; object-fit:cover; display:block; }

.co-game-body { padding:20px; }

.co-genres {
  display:flex; gap:6px; flex-wrap:wrap;
  margin-bottom:10px;
}
.co-genre-pill {
  font-size:10px; font-weight:600;
  color:#6b6b6b; letter-spacing:0.5px; text-transform:uppercase;
  background:rgba(255,255,255,0.04);
  border:1px solid rgba(255,255,255,0.08);
  border-radius:4px; padding:3px 8px;
}

.co-game-title {
  font-size:22px; font-weight:800;
  color:#fff; margin-bottom:16px;
  letter-spacing:-0.5px; line-height:1.2;
}

.co-info-rows { display:flex; flex-direction:column; }
.co-info-row {
  display:flex; justify-content:space-between; align-items:center;
  padding:11px 0;
  border-bottom:1px solid rgba(255,255,255,0.05);
  font-size:13px;
}
.co-info-row:last-child { border-bottom:none; }
.co-info-label { color:#6b6b6b; font-weight:500; }
.co-info-value { color:#a3a3a3; font-size:12px; font-weight:500; }
.co-info-value.green { color:#22c55e; }
.co-info-value.yellow { color:#eab308; }
.co-info-value.red { color:#ef4444; }

/* ── TOTAL ROW ─────────────────────────────── */
.co-total {
  background:rgba(0,120,242,0.06);
  border:1px solid rgba(0,120,242,0.2);
  border-radius:8px;
  padding:16px 20px;
  display:flex; justify-content:space-between; align-items:center;
  margin-bottom:12px;
}
.co-total-label {
  font-size:12px; font-weight:700;
  color:#6b6b6b; letter-spacing:1px; text-transform:uppercase;
}
.co-total-amount {
  font-size:28px; font-weight:900;
  color:#0078F2; letter-spacing:-1px;
}
.co-total-currency {
  font-size:13px; font-weight:600;
  color:#6b6b6b; margin-left:4px;
}

/* ── PAYMENT BADGES ────────────────────────── */
.co-badges {
  display:flex; align-items:center; gap:8px; flex-wrap:wrap;
  padding:12px 16px;
  background:rgba(255,255,255,0.02);
  border:1px solid rgba(255,255,255,0.06);
  border-radius:8px;
}
.co-badges-label { font-size:11px; color:#6b6b6b; font-weight:500; }
.co-badge {
  font-size:11px; font-weight:600;
  border-radius:4px; padding:3px 10px;
  color:#6b6b6b;
  background:rgba(255,255,255,0.04);
  border:1px solid rgba(255,255,255,0.08);
}
.co-badge.highlight {
  color:#0078F2;
  background:rgba(0,120,242,0.1);
  border-color:rgba(0,120,242,0.25);
}

/* ── RIGHT PANEL ───────────────────────────── */
.co-panel {
  background:#1a1a1a;
  border:1px solid rgba(255,255,255,0.08);
  border-radius:12px;
  overflow:hidden;
  position:sticky;
  top:80px;
}

.co-panel-head {
  padding:18px 24px;
  border-bottom:1px solid rgba(255,255,255,0.06);
  display:flex; align-items:center; gap:12px;
}
.co-panel-icon {
  width:40px; height:40px;
  background:rgba(0,120,242,0.1);
  border:1px solid rgba(0,120,242,0.2);
  border-radius:8px;
  display:flex; align-items:center; justify-content:center;
  font-size:18px; flex-shrink:0;
}
.co-panel-title {
  font-size:15px; font-weight:700; color:#fff;
}
.co-panel-sub {
  font-size:11px; color:#6b6b6b; margin-top:2px; font-weight:500;
}

.co-panel-body { padding:24px; }

/* ── BUTTONS ───────────────────────────────── */
.co-btn-pay {
  width:100%; padding:15px;
  background:#0078F2; color:#fff;
  border:none; border-radius:8px;
  font-family:'Inter',sans-serif;
  font-size:15px; font-weight:700;
  letter-spacing:0.02em;
  cursor:pointer; transition:all 0.15s;
  display:block; text-align:center; text-decoration:none;
}
.co-btn-pay:hover:not(:disabled) {
  background:#0a84ff; transform:translateY(-1px);
  box-shadow:0 8px 32px rgba(0,120,242,0.35);
}
.co-btn-pay:active:not(:disabled) { transform:translateY(0); box-shadow:none; }
.co-btn-pay:disabled { opacity:0.45; cursor:not-allowed; }

.co-btn-secondary {
  width:100%; padding:13px;
  background:rgba(255,255,255,0.06); color:#a3a3a3;
  border:1px solid rgba(255,255,255,0.1); border-radius:8px;
  font-family:'Inter',sans-serif;
  font-size:14px; font-weight:600;
  cursor:pointer; transition:all 0.15s;
}
.co-btn-secondary:hover {
  background:rgba(255,255,255,0.1); color:#fff;
  border-color:rgba(255,255,255,0.2);
}

.co-secure {
  display:flex; align-items:center; justify-content:center; gap:6px;
  margin-top:16px;
  font-size:11px; color:#6b6b6b; font-weight:500;
}

/* ── QR SECTION ────────────────────────────── */
.co-qr-wrap {
  display:flex; flex-direction:column;
  align-items:center; gap:16px;
}
.co-qr-frame {
  background:#fff; padding:14px; border-radius:12px;
  box-shadow:0 0 40px rgba(0,120,242,0.12);
  display:flex; align-items:center; justify-content:center;
}
.co-qr-frame img { width:220px; height:220px; object-fit:contain; display:block; }

.co-qr-label {
  font-size:11px; font-weight:700;
  letter-spacing:2px; color:#0078F2;
  text-transform:uppercase;
}
.co-scan-hint {
  font-size:13px; color:#6b6b6b;
  text-align:center; line-height:1.7;
}
.co-scan-hint strong { color:#a3a3a3; font-weight:600; }

.co-poll-status {
  display:flex; align-items:center; justify-content:center; gap:8px;
  width:100%; padding:10px 14px;
  background:rgba(0,120,242,0.06);
  border:1px solid rgba(0,120,242,0.15);
  border-radius:8px;
  font-size:12px; color:#6b6b6b; font-weight:500;
}
.co-poll-dot {
  width:6px; height:6px; background:#0078F2;
  border-radius:50%;
  animation:pollPulse 1.2s ease infinite;
  flex-shrink:0;
}
@keyframes pollPulse {
  0%,100%{ opacity:1; transform:scale(1); }
  50%{ opacity:0.3; transform:scale(0.6); }
}

/* ── EXPIRED / ERROR ───────────────────────── */
.co-state { text-align:center; padding:8px 0; }
.co-state-icon { font-size:44px; margin-bottom:14px; }
.co-state-title { font-size:18px; font-weight:800; color:#ef4444; margin-bottom:8px; letter-spacing:-0.3px; }
.co-state-title.orange { color:#eab308; }
.co-state-desc { font-size:13px; color:#6b6b6b; margin-bottom:24px; line-height:1.7; }

.co-error-box {
  display:flex; gap:10px; align-items:flex-start;
  padding:13px 16px;
  background:rgba(239,68,68,0.08);
  border:1px solid rgba(239,68,68,0.2);
  border-radius:8px;
  color:#ef4444; font-size:13px;
  text-align:left; margin-bottom:20px;
}

/* ── SKELETON ──────────────────────────────── */
.co-skel {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.06);
  border-radius:12px; overflow:hidden; margin-bottom:16px;
}
.co-skel-img { height:180px; background:#2a2a2a; animation:coShim 1.5s ease infinite; }
.co-skel-body { padding:20px; display:flex; flex-direction:column; gap:10px; }
.co-skel-line { height:10px; border-radius:4px; background:#2a2a2a; animation:coShim 1.5s ease infinite; }
@keyframes coShim { 0%,100%{opacity:.4} 50%{opacity:.8} }

/* ── MOBILE DEEPLINK BUTTON ────────────────── */
.mobile-only { display:none; }
@media(max-width:768px) { .mobile-only { display:block; width:100%; } }

/* ── BACK TO STORE LINK ────────────────────── */
.co-back-store {
  display:block; text-align:center; margin-top:14px;
  font-size:12px; color:#6b6b6b; font-weight:500; text-decoration:none; transition:color 0.15s;
}
.co-back-store:hover { color:#0078F2; }

/* ── LOADING DOTS ──────────────────────────── */
@keyframes ldots {
  0%{content:''} 25%{content:'.'} 50%{content:'..'} 75%,100%{content:'...'}
}
.ldots::after { content:''; animation:ldots 1.2s steps(4,end) infinite; }
`;

export default function Checkout() {
  const { gameId } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [stage, setStage] = useState("loading");
  const [orderId, setOrderId] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [deeplink, setDeeplink] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [initiating, setInitiating] = useState(false);
  const [pollStatus, setPollStatus] = useState("");
  const [paymentDots, setPaymentDots] = useState(0);

  const pollRef = useRef(null);

  useEffect(() => {
    getGame(gameId)
      .then((g) => {
        if (!g || !g.active) { setErrorMsg(t("co_unavailable")); setStage("error"); return; }
        setGame(g); setStage("confirm");
      })
      .catch(() => { setErrorMsg(t("co_load_fail")); setStage("error"); });
  }, [gameId]);

  useEffect(() => {
    if (stage !== "paying") return;
    const id = setInterval(() => setPaymentDots(d => (d + 1) % 4), 500);
    return () => clearInterval(id);
  }, [stage]);

  const startPolling = useCallback((oId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const result = await pollPaymentStatus(oId);
        if (result.status === "delivered") {
          clearInterval(pollRef.current);
          navigate(`/order-success/${oId}`, { replace: true });
        } else if (result.status === "expired" || result.status === "failed") {
          clearInterval(pollRef.current); setStage("expired");
        } else if (result.status === "paid") {
          setPollStatus(t("co_poll_confirmed"));
        } else {
          setPollStatus(t("co_poll_waiting"));
        }
      } catch (err) { console.error("Poll error:", err); }
    }, 3000);
  }, [navigate, t]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  async function handlePay() {
    setInitiating(true); setErrorMsg("");
    try {
      const result = await initiatePayment(gameId);
      setOrderId(result.orderId); setExpiresAt(result.expiresAt);
      setQrImage(result.qrImage); setDeeplink(result.deeplink);
      setStage("paying"); startPolling(result.orderId);
    } catch (err) {
      const msg = err?.message || t("co_pay_fail");
      if (msg.includes("out of stock") || msg.includes("resource-exhausted")) {
        setErrorMsg(t("co_out_of_stock_err"));
      } else if (msg.includes("unauthenticated")) {
        navigate("/login", { state: { from: `/checkout/${gameId}` } }); return;
      } else { setErrorMsg(msg); }
      setStage("error");
    } finally { setInitiating(false); }
  }

  function handleQRExpired() { if (pollRef.current) clearInterval(pollRef.current); setStage("expired"); }

  function handleRetry() {
    setStage("confirm"); setQrImage(null); setDeeplink(null);
    setOrderId(null); setExpiresAt(null); setErrorMsg("");
  }

  const stockColor = game
    ? game.stock <= 0 ? "red" : game.stock <= 2 ? "yellow" : "green"
    : "green";
  const stockLabel = game
    ? game.stock <= 0 ? t("out_of_stock") : game.stock <= 2 ? `⚠️ ${t("co_only")} ${game.stock} ${t("co_left")}` : `✅ ${game.stock} ${t("co_available")}`
    : "";

  return (
    <>
      <style>{S}</style>
      <div className="co-page">
        <div className="co-bg-grid" />
        <div className="co-bg-glow" />
        <Navbar />

        <div className="co-body">

          {/* ── LEFT: Order Summary ─────────────────── */}
          <div className="co-left">
            <Link to={`/game/${gameId}`} className="co-back">← {t("back_store").replace("← ", "")}</Link>
            <div className="co-sect-tag">{t("co_order_summary")}</div>

            {stage === "loading" ? (
              <div className="co-skel">
                <div className="co-skel-img" />
                <div className="co-skel-body">
                  <div className="co-skel-line" style={{ width: "40%" }} />
                  <div className="co-skel-line" style={{ width: "65%" }} />
                  <div className="co-skel-line" />
                  <div className="co-skel-line" style={{ width: "50%" }} />
                </div>
              </div>
            ) : game && (
              <>
                <div className="co-game-card">
                  <div className="co-cover">
                    {game.coverImage ? <img src={game.coverImage} alt={game.title} /> : "🎮"}
                  </div>
                  <div className="co-game-body">
                    <div className="co-genres">
                      {(game.genre || []).slice(0, 3).map(g => (
                        <span key={g} className="co-genre-pill">{g}</span>
                      ))}
                    </div>
                    <div className="co-game-title">{game.title}</div>
                    <div className="co-info-rows">
                      <div className="co-info-row">
                        <span className="co-info-label">{t("type_label")}</span>
                        <span className="co-info-value">{t("acc_shared")}</span>
                      </div>
                      <div className="co-info-row">
                        <span className="co-info-label">{t("delivery_label")}</span>
                        <span className="co-info-value green">{t("delivery_val")} {t("instant_delivery")}</span>
                      </div>
                      <div className="co-info-row">
                        <span className="co-info-label">{t("stock_label")}</span>
                        <span className={`co-info-value ${stockColor}`}>{stockLabel}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="co-total">
                  <span className="co-total-label">{t("co_total")}</span>
                  <div>
                    <span className="co-total-amount">${game.price?.toFixed(2)}</span>
                    <span className="co-total-currency">USD</span>
                  </div>
                </div>

                <div className="co-badges">
                  <span className="co-badges-label">{t("co_powered_by")}:</span>
                  <span className="co-badge highlight">ABA PayWay</span>
                  <span className="co-badge">KHQR</span>
                  <span className="co-badge">Bakong</span>
                </div>
              </>
            )}
          </div>

          {/* ── RIGHT: Payment Panel ────────────────── */}
          <div className="co-right">
            <div className="co-panel">
              <div className="co-panel-head">
                <div className="co-panel-icon">
                  {stage === "paying" ? "📱" : stage === "expired" ? "⏰" : stage === "error" ? "⚠️" : "💳"}
                </div>
                <div>
                  <div className="co-panel-title">
                    {stage === "paying"  ? t("co_scan_to_pay") :
                     stage === "expired" ? t("co_expired_title") :
                     stage === "error"   ? t("co_error_title") :
                                          t("co_secure_checkout")}
                  </div>
                  <div className="co-panel-sub">
                    {stage === "paying" ? "ABA Mobile · KHQR" : t("co_powered_by") + " ABA PayWay"}
                  </div>
                </div>
              </div>

              <div className="co-panel-body">

                {/* CONFIRM */}
                {stage === "confirm" && game && (
                  <>
                    <button
                      className="co-btn-pay"
                      onClick={handlePay}
                      disabled={initiating || game.stock <= 0}
                    >
                      {initiating
                        ? <span className="ldots">{t("co_generating_qr")}</span>
                        : game.stock <= 0
                        ? t("out_of_stock")
                        : `${t("co_pay_now")} $${game.price?.toFixed(2)}`}
                    </button>
                    <div className="co-secure">🔒 {t("co_secured_by")}</div>
                  </>
                )}

                {/* PAYING — QR */}
                {stage === "paying" && qrImage && (
                  <div className="co-qr-wrap">
                    <PaymentTimer expiresAt={expiresAt} onExpired={handleQRExpired} />
                    <div className="co-qr-frame">
                      <img src={qrImage} alt="ABA KHQR" onError={e => console.error("QR failed:", e)} />
                    </div>
                    <div className="co-qr-label">ABA PAYWAY KHQR</div>
                    <div className="co-scan-hint">
                      {t("co_scan_with")} <strong>ABA Mobile</strong> {t("co_or_banking")}.<br />
                      {t("co_amount")}: <strong style={{ color:"#fff" }}>${game?.price?.toFixed(2)} USD</strong>
                    </div>
                    {deeplink && (
                      <div className="mobile-only">
                        <a href={deeplink} className="co-btn-pay" style={{ fontSize:13, padding:"11px" }}>
                          {t("co_open_aba")} ↗
                        </a>
                      </div>
                    )}
                    <div className="co-poll-status">
                      <div className="co-poll-dot" />
                      {pollStatus || t("co_poll_waiting")}{"...".slice(0, paymentDots + 1)}
                    </div>
                  </div>
                )}

                {/* EXPIRED */}
                {stage === "expired" && (
                  <div className="co-state">
                    <div className="co-state-icon">⏰</div>
                    <div className="co-state-title orange">{t("co_expired_title")}</div>
                    <div className="co-state-desc">{t("co_expired_desc")}</div>
                    <button className="co-btn-pay" onClick={handleRetry}>{t("co_new_qr")} →</button>
                  </div>
                )}

                {/* ERROR */}
                {stage === "error" && (
                  <div className="co-state">
                    <div className="co-error-box">
                      <span>⚠️</span>
                      <span>{errorMsg || t("co_generic_error")}</span>
                    </div>
                    <button className="co-btn-secondary" onClick={handleRetry}>{t("co_try_again")}</button>
                    <Link to="/store" className="co-back-store">← {t("nav_store")}</Link>
                  </div>
                )}

                {/* LOADING */}
                {stage === "loading" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="co-skel-line" style={{ height:14, width: i%2===0?"100%":"65%", borderRadius:6, background:"#2a2a2a", animation:"coShim 1.5s ease infinite" }} />
                    ))}
                    <div style={{ height:48, marginTop:16, borderRadius:8, background:"#2a2a2a", animation:"coShim 1.5s ease infinite" }} />
                  </div>
                )}

              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}